/**
 * The seller's side of bulk upload: turning a spreadsheet into the rows the
 * API takes, splitting them into requests, and writing CSVs back out.
 *
 * Kept free of React and of the network so it can be tested on its own. The
 * server checks every row again — what is caught here is only what can be
 * caught from the sheet alone (a missing column, "abc" in a price cell), so
 * the seller hears about it before anything is sent.
 */
import Papa from 'papaparse';

import type { catalog } from '@terra/contracts';
import { parseRupeesToPaise } from '@terra/utils';

// ── Columns ─────────────────────────────────────────────────────────────────

/** Headers a seller might write, squashed (lowercase, letters and digits only) → field. */
const PRODUCT_ALIASES: Record<string, string> = {
  handle: 'handle',
  producthandle: 'handle',
  group: 'handle',
  parentsku: 'handle',
  title: 'title',
  name: 'title',
  productname: 'title',
  producttitle: 'title',
  category: 'category',
  categoryslug: 'category',
  brand: 'brand',
  description: 'description',
  bulletpoints: 'bulletPoints',
  bullets: 'bulletPoints',
  highlights: 'bulletPoints',
  hsn: 'hsnSac',
  hsnsac: 'hsnSac',
  hsncode: 'hsnSac',
  gst: 'gstRate',
  gstrate: 'gstRate',
  gstpercent: 'gstRate',
  sku: 'sku',
  mrp: 'mrp',
  mrpinr: 'mrp',
  price: 'price',
  sellingprice: 'price',
  saleprice: 'price',
  weight: 'weightGrams',
  weightg: 'weightGrams',
  weightgrams: 'weightGrams',
  barcode: 'barcode',
  ean: 'barcode',
  stock: 'stock',
  quantity: 'stock',
  qty: 'stock',
  openingstock: 'stock',
  options: 'options',
  variant: 'options',
  variantoptions: 'options',
  images: 'imageUrls',
  imageurls: 'imageUrls',
  imagelinks: 'imageUrls',
};

const STOCK_ALIASES: Record<string, string> = {
  sku: 'sku',
  warehouse: 'warehouse',
  location: 'warehouse',
  quantity: 'quantity',
  qty: 'quantity',
  stock: 'quantity',
  onhand: 'quantity',
};

export const PRODUCT_TEMPLATE_HEADERS = [
  'handle',
  'title',
  'category',
  'brand',
  'description',
  'bullet_points',
  'hsn',
  'gst_rate',
  'sku',
  'mrp',
  'price',
  'weight_grams',
  'barcode',
  'stock',
  'options',
  'image_urls',
];

const REQUIRED_PRODUCT_FIELDS: Array<[string, string]> = [
  ['title', 'title'],
  ['category', 'category'],
  ['hsnSac', 'hsn'],
  ['sku', 'sku'],
  ['mrp', 'mrp'],
  ['price', 'price'],
];

const squash = (h: string) => h.toLowerCase().replace(/[^a-z0-9]/g, '');

/** Byte-order mark: Excel writes it, and needs it to read a UTF-8 CSV (₹, Hindi) correctly. */
export const BOM = String.fromCharCode(0xfeff);

// ── Parsing ─────────────────────────────────────────────────────────────────

export interface SheetIssue {
  line: number;
  message: string;
}

export interface ParsedSheet<T> {
  rows: T[];
  /** Problems found in the sheet itself, before anything is sent. */
  issues: SheetIssue[];
  /** A problem with the file as a whole (no header row, a missing column). */
  fatal: string | null;
}

function readCsv(text: string): { header: string[]; records: string[][] } {
  const body = text.startsWith(BOM) ? text.slice(1) : text;
  const parsed = Papa.parse<string[]>(body, { skipEmptyLines: false });
  const [header = [], ...records] = parsed.data;
  return { header, records };
}

function mapHeader(header: string[], aliases: Record<string, string>) {
  return header.map((h) => aliases[squash(h)] ?? null);
}

const isBlank = (cells: string[]) => cells.every((c) => !c?.trim());

/** `"Size=M | Colour: Red"` → `{ Size: 'M', Colour: 'Red' }`; `null` if a part has no value. */
export function parseOptions(cell: string): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const part of cell
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean)) {
    const m = /^([^=:]+)[=:](.+)$/.exec(part);
    if (!m) return null;
    out[m[1]!.trim()] = m[2]!.trim();
  }
  return out;
}

function parseWhole(cell: string): number | null {
  const s = cell.trim().replace(/,/g, '');
  return /^\d+$/.test(s) ? Number(s) : null;
}

export function parseProductSheet(text: string): ParsedSheet<catalog.BulkProductRow> {
  const { header, records } = readCsv(text);
  const fields = mapHeader(header, PRODUCT_ALIASES);
  const missing = REQUIRED_PRODUCT_FIELDS.filter(([f]) => !fields.includes(f)).map(([, h]) => h);
  if (header.length === 0 || missing.length > 0) {
    return {
      rows: [],
      issues: [],
      fatal:
        header.length === 0
          ? 'The file is empty.'
          : `Missing column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Start from the template.`,
    };
  }

  const rows: catalog.BulkProductRow[] = [];
  const issues: SheetIssue[] = [];
  const handleOfLine = new Map<number, string>();
  const seenHandles = new Set<string>();
  records.forEach((cells, i) => {
    const line = i + 2; // line 1 is the header
    if (isBlank(cells)) return;
    const get = (field: string) => {
      const idx = fields.indexOf(field);
      return idx === -1 ? '' : (cells[idx] ?? '').trim();
    };
    const problems: string[] = [];

    // Product details come from the first row of a handle; later rows of the
    // same handle are variants and only need their own SKU and prices.
    const handle = get('handle').toLowerCase();
    const isFirstOfProduct = !handle || !seenHandles.has(handle);
    if (handle) {
      seenHandles.add(handle);
      handleOfLine.set(line, handle);
    }
    for (const [field, label] of REQUIRED_PRODUCT_FIELDS) {
      const productLevel = field === 'title' || field === 'category' || field === 'hsnSac';
      if (productLevel && !isFirstOfProduct) continue;
      if (!get(field)) problems.push(`${label} is empty`);
    }
    const mrpPaise = get('mrp') ? parseRupeesToPaise(get('mrp')) : null;
    const pricePaise = get('price') ? parseRupeesToPaise(get('price')) : null;
    if (get('mrp') && mrpPaise === null)
      problems.push(`mrp “${get('mrp')}” is not an amount like 499 or 499.50`);
    if (get('price') && pricePaise === null)
      problems.push(`price “${get('price')}” is not an amount like 499 or 499.50`);

    const gstCell = get('gstRate').replace(/%$/, '').trim();
    const gstRate = gstCell ? Number(gstCell) : undefined;
    if (gstCell && !Number.isFinite(gstRate))
      problems.push(`gst_rate “${get('gstRate')}” is not a number`);

    const whole = (field: string, label: string) => {
      const cell = get(field);
      if (!cell) return undefined;
      const n = parseWhole(cell);
      if (n === null) problems.push(`${label} “${cell}” must be a whole number`);
      return n ?? undefined;
    };
    const weightGrams = whole('weightGrams', 'weight_grams');
    const stock = whole('stock', 'stock');

    const options = get('options') ? parseOptions(get('options')) : {};
    if (options === null)
      problems.push(`options “${get('options')}” should look like Size=M|Colour=Red`);

    // Images belong to the product, so only its first row's are read.
    const imageUrls = isFirstOfProduct
      ? get('imageUrls')
          .split(/[\s|,]+/)
          .filter(Boolean)
      : [];
    if (imageUrls.length > 4) problems.push('at most 4 image links per product');

    if (problems.length > 0) {
      issues.push(...problems.map((message) => ({ line, message })));
      return;
    }

    const bullets = get('bulletPoints')
      .split('|')
      .map((b) => b.trim())
      .filter(Boolean);
    rows.push({
      line,
      title: get('title'),
      category: get('category'),
      hsnSac: get('hsnSac'),
      sku: get('sku'),
      mrpPaise: mrpPaise!,
      pricePaise: pricePaise!,
      ...(get('handle') ? { handle: get('handle') } : {}),
      ...(get('brand') ? { brand: get('brand') } : {}),
      ...(get('description') ? { description: get('description') } : {}),
      ...(bullets.length ? { bulletPoints: bullets.slice(0, 10) } : {}),
      ...(gstRate !== undefined ? { gstRate } : {}),
      ...(weightGrams !== undefined ? { weightGrams } : {}),
      ...(get('barcode') ? { barcode: get('barcode') } : {}),
      ...(stock !== undefined ? { stock } : {}),
      ...(options && Object.keys(options).length ? { options } : {}),
      ...(imageUrls.length ? { imageUrls } : {}),
    });
  });

  // A SKU twice in one file: caught here because the file goes up in pieces
  // and the server only compares rows within one piece.
  const firstLine = new Map<string, number>();
  const clean: catalog.BulkProductRow[] = [];
  for (const r of rows) {
    const key = r.sku.toLowerCase();
    const first = firstLine.get(key);
    if (first !== undefined) {
      issues.push({ line: r.line, message: `SKU ${r.sku} is already used on line ${first}` });
    } else {
      firstLine.set(key, r.line);
      clean.push(r);
    }
  }

  // A product is sent whole or not at all — drop every row of a handle that
  // lost one of its rows above.
  const brokenHandles = new Set(
    issues.map((i) => handleOfLine.get(i.line)).filter((h): h is string => Boolean(h)),
  );
  const kept: catalog.BulkProductRow[] = [];
  for (const r of clean) {
    const handle = r.handle?.trim().toLowerCase();
    if (handle && brokenHandles.has(handle)) {
      issues.push({ line: r.line, message: 'Not sent: another row of this product has a problem' });
    } else {
      kept.push(r);
    }
  }

  issues.sort((a, b) => a.line - b.line);
  return { rows: kept, issues, fatal: null };
}

export function parseStockSheet(text: string): ParsedSheet<catalog.BulkStockRow> {
  const { header, records } = readCsv(text);
  const fields = mapHeader(header, STOCK_ALIASES);
  const missing = ['sku', 'quantity'].filter((f) => !fields.includes(f));
  if (header.length === 0 || missing.length > 0) {
    return {
      rows: [],
      issues: [],
      fatal:
        header.length === 0
          ? 'The file is empty.'
          : `Missing column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. Start from the stock template.`,
    };
  }
  const rows: catalog.BulkStockRow[] = [];
  const issues: SheetIssue[] = [];
  records.forEach((cells, i) => {
    const line = i + 2;
    if (isBlank(cells)) return;
    const get = (field: string) => {
      const idx = fields.indexOf(field);
      return idx === -1 ? '' : (cells[idx] ?? '').trim();
    };
    const sku = get('sku');
    const quantity = parseWhole(get('quantity'));
    if (!sku) issues.push({ line, message: 'sku is empty' });
    if (quantity === null) {
      issues.push({
        line,
        message: `quantity “${get('quantity')}” must be a whole number (0 or more)`,
      });
    }
    if (!sku || quantity === null) return;
    rows.push({
      line,
      sku,
      quantity,
      ...(get('warehouse') ? { warehouse: get('warehouse') } : {}),
    });
  });
  return { rows, issues, fatal: null };
}

// ── Splitting into requests ─────────────────────────────────────────────────

export const CHUNK_LIMITS = { rows: 100, images: 40, products: 20 };

/**
 * Split rows into requests without ever splitting a product: the rows of one
 * handle always travel together, because the server builds a product's
 * variants from the rows it is given.
 */
export function chunkProductRows(
  rows: catalog.BulkProductRow[],
  limits = CHUNK_LIMITS,
): catalog.BulkProductRow[][] {
  const groups: catalog.BulkProductRow[][] = [];
  const byHandle = new Map<string, catalog.BulkProductRow[]>();
  for (const r of rows) {
    const h = r.handle?.trim().toLowerCase();
    const g = h ? byHandle.get(h) : undefined;
    if (g) {
      g.push(r);
      continue;
    }
    const fresh = [r];
    groups.push(fresh);
    if (h) byHandle.set(h, fresh);
  }

  const chunks: catalog.BulkProductRow[][] = [];
  let current: catalog.BulkProductRow[] = [];
  let images = 0;
  let products = 0;
  for (const g of groups) {
    // Counted over every row, as the server counts them against its per-request cap.
    const gImages = g.reduce((n, r) => n + (r.imageUrls?.length ?? 0), 0);
    const full =
      current.length + g.length > limits.rows ||
      images + gImages > limits.images ||
      products + 1 > limits.products;
    if (current.length > 0 && full) {
      chunks.push(current);
      current = [];
      images = 0;
      products = 0;
    }
    current.push(...g);
    images += gImages;
    products += 1;
  }
  if (current.length > 0) chunks.push(current);
  return chunks;
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

// ── Writing CSVs ────────────────────────────────────────────────────────────

/** One CSV cell, quoted, with a leading formula character neutralised for spreadsheets. */
function cell(v: unknown): string {
  const s = String(v ?? '');
  return `"${(/^[=+\-@]/.test(s) ? `'${s}` : s).replace(/"/g, '""')}"`;
}

export function toCsv(rows: unknown[][]): string {
  return rows.map((r) => r.map(cell).join(',')).join('\r\n');
}

export function downloadCsv(name: string, rows: unknown[][]) {
  const url = URL.createObjectURL(
    new Blob([BOM + toCsv(rows)], { type: 'text/csv;charset=utf-8' }),
  );
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export const PRODUCT_SAMPLE_ROWS: string[][] = [
  [
    'kurta-01',
    'Cotton Kurta',
    'Kurtas',
    'Terra',
    'Soft cotton kurta',
    'Pure cotton|Machine wash',
    '6109',
    '5',
    'KUR-01-M',
    '999',
    '799',
    '250',
    '',
    '10',
    'Size=M',
    'https://example.com/kurta-front.jpg',
  ],
  ['kurta-01', '', '', '', '', '', '', '', 'KUR-01-L', '999', '799', '260', '', '8', 'Size=L', ''],
  [
    '',
    'Steel Water Bottle 1L',
    'Bottles',
    '',
    'Keeps water cold for 12 hours',
    '',
    '7323',
    '18',
    'BTL-1L',
    '599',
    '449',
    '400',
    '8901234567890',
    '25',
    '',
    '',
  ],
];
